import type { Component } from 'vue'

import { Building07Icon, Download01Icon, Home01Icon, Target04Icon, Users01Icon, UsersPlusIcon } from '@gohighlevel/ghl-icons/24/outline'
import { renderIcon } from '@platform-ui/highrise'

import type { MenuOption } from '@ui/types/menu'

import { EXHIBITOR_CENTER_DEFAULT_SECTION, EXHIBITOR_CENTER_SECTION_IDS } from '../constants/exhibitor-center.constants'
import type { ExhibitorCenterNavGroup, ExhibitorCenterSectionId } from '../types/exhibitor-center'

export const isExhibitorCenterSectionId = (value: string): value is ExhibitorCenterSectionId =>
  EXHIBITOR_CENTER_SECTION_IDS.includes(value as ExhibitorCenterSectionId)

export const toExhibitorCenterSectionId = (value: string): ExhibitorCenterSectionId => {
  const sectionId = value.trim()
  return isExhibitorCenterSectionId(sectionId) ? sectionId : EXHIBITOR_CENTER_DEFAULT_SECTION
}

type Translate = (key: string) => string

const SECTION_LABEL_KEYS = {
  overview: 'clientportal.events.detail.exhibitorCenterNavOverview',
  team: 'clientportal.events.detail.exhibitorCenterNavTeam',
  leads: 'clientportal.events.detail.exhibitorCenterNavConnection',
  export: 'clientportal.events.detail.exhibitorCenterNavExport',
} as const satisfies Record<ExhibitorCenterSectionId, string>

const SECTION_ICONS = {
  overview: Home01Icon,
  team: Users01Icon,
  leads: UsersPlusIcon,
  export: Download01Icon,
} as const satisfies Record<ExhibitorCenterSectionId, Component>

/** One nav definition behind the desktop rail and the compact nav, both rendered with CPMenu. */
const NAV_GROUPS = [
  {
    key: 'company',
    labelKey: 'clientportal.events.detail.exhibitorCenterNavCompany',
    icon: Building07Icon,
    sectionIds: ['overview', 'team'],
  },
  { key: 'leads', labelKey: 'clientportal.events.detail.exhibitorCenterNavLeads', icon: Target04Icon, sectionIds: ['leads', 'export'] },
] as const satisfies readonly { key: string; labelKey: string; icon: Component; sectionIds: readonly ExhibitorCenterSectionId[] }[]

/** CPMenu keys share one namespace with the section leaves, so a group is suffixed to keep `leads` unique. */
const menuGroupKey = (groupKey: string) => `${groupKey}-group`

export const EXHIBITOR_CENTER_MENU_GROUP_KEYS = NAV_GROUPS.map(group => menuGroupKey(group.key))

export const exhibitorCenterSectionLabel = (t: Translate, sectionId: string): string =>
  t(SECTION_LABEL_KEYS[toExhibitorCenterSectionId(sectionId)])

export const buildExhibitorCenterNavGroups = (t: Translate): readonly ExhibitorCenterNavGroup[] =>
  NAV_GROUPS.map(group => ({
    key: group.key,
    label: t(group.labelKey),
    icon: group.icon,
    sections: group.sectionIds.map(id => ({ id, label: t(SECTION_LABEL_KEYS[id]), icon: SECTION_ICONS[id] })),
  }))

export const buildExhibitorCenterMenuOptions = (t: Translate): MenuOption[] =>
  buildExhibitorCenterNavGroups(t).map(group => ({
    key: menuGroupKey(group.key),
    title: group.label,
    icon: renderIcon(group.icon),
    children: group.sections.map(section => ({ key: section.id, title: section.label, icon: renderIcon(section.icon) })),
  }))
